import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Map, GitCompare } from 'lucide-react';
import { v3Api } from '../hooks/useApi';
import CardInfo from './CardInfo';

/**
 * DigitalTwin — placeholder card. D3 owns full /api/twin/:propertyId visualization.
 * E1 only does a light fetch to show propertyId + unit count, with a link out.
 *
 * Sprint 3.5 Stream B: soft-link to ComparisonTool when ≥2 zones exist for the
 * property. Coupling is one-way via URL params (?entityAType=Zone&entityAId=...).
 */
export default function DigitalTwin({ propertyId }) {
    const { t } = useTranslation();
    const [unitCount, setUnitCount] = useState(null);
    const [propName, setPropName] = useState(null);
    const [err, setErr] = useState(null);
    const [zones, setZones] = useState([]);

    useEffect(() => {
        if (!propertyId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await v3Api.twin.property(propertyId);
                if (cancelled) return;
                setUnitCount(Array.isArray(res?.data?.units) ? res.data.units.length : 0);
                setPropName(res?.data?.property?.name || null);
            } catch (e) {
                if (!cancelled) setErr(e.response?.data?.error || e.message);
            }
        })();
        return () => { cancelled = true; };
    }, [propertyId]);

    useEffect(() => {
        if (!propertyId) { setZones([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await v3Api.zone.list(propertyId);
                if (cancelled) return;
                setZones(Array.isArray(res?.data) ? res.data : []);
            } catch {
                if (!cancelled) setZones([]);
            }
        })();
        return () => { cancelled = true; };
    }, [propertyId]);

    const canCompare = zones.length >= 2;
    const compareHref = canCompare
        ? `${process.env.PUBLIC_URL}/v3/compare?entityAType=Zone&entityAId=${encodeURIComponent(zones[0])}&entityBType=Zone&entityBId=${encodeURIComponent(zones[1])}`
        : null;

    return (
        <div className="v3-card v3-cc-card">
            <div className="v3-cc-card-header-row">
                <h3>{t('Digital Twin')}</h3>
                <CardInfo title={t('About Digital Twin')}>
                    <p>
                        {t('This card is a gateway to the')} <strong>{t('Digital Twin floor-plan view')}</strong> — {t('an interactive 2D/3D map of the property with per-unit KPIs.')}
                    </p>
                    <p>
                        <strong>{t('Units')}</strong> {t('is the number of mapped units (tenant spaces) on the floor plan.')} <strong>{t('Zones')}</strong> {t('is the number of defined zones in the property.')}
                    </p>
                    <p>
                        {t('Open the floor-plan view to inspect each unit’s OCR%, year-over-year growth, total sales, and open anomalies on the map.')}
                    </p>
                    <p>
                        {t('When a property has two or more zones, you can jump straight into a zone-vs-zone comparison.')}
                    </p>
                </CardInfo>
            </div>
            <div className="v3-cc-twin">
                <div className="meta-row">
                    <div>
                        <span className="meta-label">{t('Property')}</span>
                        <strong>{propName || propertyId || '—'}</strong>
                    </div>
                    <div>
                        <span className="meta-label">{t('Units')}</span>
                        <strong>{unitCount != null ? unitCount : '—'}</strong>
                    </div>
                    <div>
                        <span className="meta-label">{t('Zones')}</span>
                        <strong>{zones.length || '—'}</strong>
                    </div>
                </div>
                {err && <div className="v3-text-danger" style={{ fontSize: 11 }}>{t('Twin unavailable')}: {err}</div>}
                {propertyId ? (
                    <a
                        href={`${process.env.PUBLIC_URL}/v3/twin/${propertyId}`}
                        className="v3-btn v3-btn-primary"
                        style={{ textDecoration: 'none' }}
                    >
                        <Map size={14} />
                        {t('Open Floor Plan View')}
                    </a>
                ) : (
                    <div className="v3-text-muted" style={{ fontSize: 12 }}>
                        {t('Select a property to enable the floor-plan view.')}
                    </div>
                )}
                {canCompare && (
                    <a
                        href={compareHref}
                        className="v3-btn"
                        style={{ textDecoration: 'none', marginTop: 8 }}
                    >
                        <GitCompare size={14} />
                        {t('Compare zones ({{zoneA}} vs {{zoneB}})', { zoneA: zones[0], zoneB: zones[1] })}
                    </a>
                )}
            </div>
        </div>
    );
}
